import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Chip,
  Alert,
  Badge
} from '@mui/material';
import {
  Add,
  CheckCircle,
  CheckCircleOutline,
  PendingActions,
  Download,
  Visibility,
  Edit,
  Cancel,
  UploadFile
} from '@mui/icons-material';
import WorkOrderSecondPageDialog from '../WorkOrderSecondPageDialog';
import { canViewActionTakenForm } from '../MaintenanceWorkflowStatusCard';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MaintenanceWorkflowStatusCard from '../MaintenanceWorkflowStatusCard';
import QuickAuditTrail from '../../common/QuickAuditTrail';
import LinkedWorkOrders from '../LinkedWorkOrders';
import AMPChecksTab from './AMPChecksTab';
import RequiredToolsEquipmentManager from './RequiredToolsEquipmentManager';
import RequiredSparesManager from './RequiredSparesManager';
import RequiredConsumablesManager from './RequiredConsumablesManager';
import VerificationChecksManager from './VerificationChecksManager';
import CamoQmReviewTab from './CamoQmReviewTab';
import { getStatusColor, getPriorityColor, WO_DETAIL_TABS } from '../utils/maintenanceUtils';
import { useToast } from '../../../contexts/ToastContext';
import { getWorkOrderDisplayNumber } from '../../../utils/workOrderDisplayNumber';
import {
  downloadAuthenticatedPdf,
  openAuthenticatedPdfViaToken,
  viewAuthenticatedPdf
} from '../../../utils/authenticatedPdf';
import { getMaintenanceWorkOrderTypeLabel as getTypeLabel } from '../../../constants/maintenanceWorkOrderTypes';

/**
 * Detail dialog for viewing work order details with tabs
 */
const MaintenanceDetailDialog = ({
  open,
  onClose,
  selectedMaintenance,
  detailTab,
  setDetailTab,
  user,
  displayTimezone,
  canAcknowledge,
  canViewCamoQmReviewTab,
  canPlan,
  canProvideProcurementRequirements,
  canStoreAction,
  canCreatePartsProcurement,
  canExternalAgencyAction,
  canMROAction,
  canMROAssignEngineers,
  canMROHandover,
  canMROEngineerComplete,
  canMROQMCompletionAck,
  canCAMOQMCompletionAck,
  canComplete,
  canResumeFromHold,
  canIssueReleaseCertificate,
  canEditWorkOrder,
  canCancelWorkOrder,
  handleWorkflowAction,
  handleWorkflowSuccess,
  handleEdit,
  handleCancel,
  setDetailOpen,
  setPlanningOpen,
  onOpenMroHandover,
  onOpenMroAssignEngineers,
  onRefresh
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [actionTakenFormOpen, setActionTakenFormOpen] = useState(false);
  const [viewingActionTakenPdf, setViewingActionTakenPdf] = useState(false);
  const [downloadingActionTakenPdf, setDownloadingActionTakenPdf] = useState(false);
  const [downloadingCameFormPdf, setDownloadingCameFormPdf] = useState(false);

  const handleWorkOrderRefreshFromForm = async (updated) => {
    if (typeof onRefresh === 'function') {
      await onRefresh(updated);
    }
  };

  const actionTakenPdfFilename = selectedMaintenance
    ? `Action-Taken-Form_${(getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id)
        .toString()
        .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`
    : 'Action-Taken-Form.pdf';

  const handleViewActionTakenPdf = async () => {
    if (!selectedMaintenance?._id) return;
    const url = `/api/maintenance/${selectedMaintenance._id}/action-taken-form.pdf`;
    setViewingActionTakenPdf(true);
    try {
      await viewAuthenticatedPdf(url);
    } catch (err) {
      try {
        openAuthenticatedPdfViaToken(url);
      } catch (fallbackErr) {
        showToast(err.message || fallbackErr.message || 'Failed to open Action Taken Form PDF', 'error');
      }
    } finally {
      setViewingActionTakenPdf(false);
    }
  };

  const handleDownloadActionTakenPdf = async () => {
    if (!selectedMaintenance?._id) return;
    const url = `/api/maintenance/${selectedMaintenance._id}/action-taken-form.pdf?download=1`;
    setDownloadingActionTakenPdf(true);
    try {
      await downloadAuthenticatedPdf(url, actionTakenPdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download Action Taken Form PDF', 'error');
    } finally {
      setDownloadingActionTakenPdf(false);
    }
  };

  const cameFormPdfFilename = selectedMaintenance
    ? `CAME-FORM-04_${(getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id)
        .toString()
        .replace(/[^a-zA-Z0-9._-]+/g, '_')}.pdf`
    : 'CAME-FORM-04.pdf';

  const handleDownloadCameFormPdf = async () => {
    if (!selectedMaintenance?._id) return;
    const url = `/api/maintenance/${selectedMaintenance._id}/came-work-order-form.pdf?download=1`;
    setDownloadingCameFormPdf(true);
    try {
      await downloadAuthenticatedPdf(url, cameFormPdfFilename);
    } catch (err) {
      showToast(err.message || 'Failed to download CAME/FORM/04 PDF', 'error');
    } finally {
      setDownloadingCameFormPdf(false);
    }
  };

  const handleAuthenticatedDownload = async (url, filenameFallback) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filenameFallback);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showToast(error.response?.data?.message || 'Download failed', 'error');
    }
  };

  const handleComponentUpdate = async () => {
    if (!selectedMaintenance) return;
    setRefreshing(true);
    try {
      const response = await axios.get(`/api/maintenance/${selectedMaintenance._id}`);
      if (onRefresh) {
        onRefresh(response.data);
      }
    } catch (error) {
      console.error('Error refreshing work order:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const hasAMPChecks = Boolean(selectedMaintenance?.ampChecks?.length);
  const showCamoQmReviewTab = selectedMaintenance && canViewCamoQmReviewTab?.(selectedMaintenance);
  const qmActionRequired = selectedMaintenance
    && (canAcknowledge(selectedMaintenance) || canCAMOQMCompletionAck(selectedMaintenance));

  useEffect(() => {
    if (!open || !selectedMaintenance || !showCamoQmReviewTab || !qmActionRequired) return;
    setDetailTab(WO_DETAIL_TABS.CAMO_QM_REVIEW);
  }, [open, selectedMaintenance?._id, showCamoQmReviewTab, qmActionRequired, setDetailTab]);

  if (!selectedMaintenance) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <span>Work Order Details: {selectedMaintenance.title}</span>
        {selectedMaintenance.standaloneEmergencyRepair && (
          <Chip size="small" color="warning" label="Stand-alone emergency WO" />
        )}
        {qmActionRequired && (
          <Chip size="small" color="warning" label="CAMO QM action required" />
        )}
      </DialogTitle>
      <DialogContent>
        <Box>
          <Tabs
            value={detailTab}
            onChange={(e, newValue) => setDetailTab(newValue)}
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value={WO_DETAIL_TABS.OVERVIEW} label="Overview" />
            {showCamoQmReviewTab && (
              <Tab
                value={WO_DETAIL_TABS.CAMO_QM_REVIEW}
                label={(
                  <Badge
                    color="warning"
                    variant="dot"
                    invisible={!qmActionRequired}
                    sx={{ '& .MuiBadge-badge': { right: -4, top: 4 } }}
                  >
                    <Box component="span" sx={{ fontWeight: qmActionRequired ? 700 : 600, pr: qmActionRequired ? 1 : 0 }}>
                      CAMO QM Review
                    </Box>
                  </Badge>
                )}
                sx={{
                  color: qmActionRequired ? 'warning.dark' : 'primary.main',
                  '&.Mui-selected': {
                    color: qmActionRequired ? 'warning.dark' : 'primary.main',
                    bgcolor: qmActionRequired ? 'warning.light' : undefined
                  }
                }}
              />
            )}
            <Tab value={WO_DETAIL_TABS.WORKFLOW} label="Workflow Status" />
            <Tab value={WO_DETAIL_TABS.LINKED} label="Linked WOs" />
            {hasAMPChecks && (
              <Tab value={WO_DETAIL_TABS.AMP_CHECKS} label="Checklist" />
            )}
            <Tab value={WO_DETAIL_TABS.TOOLS} label="Tools & Equipment" />
            <Tab value={WO_DETAIL_TABS.SPARES} label="Spares" />
            <Tab value={WO_DETAIL_TABS.CONSUMABLES} label="Consumables" />
            <Tab value={WO_DETAIL_TABS.VERIFICATION} label="Verification Checks" />
            <Tab value={WO_DETAIL_TABS.ACTIONS} label="Actions" />
          </Tabs>

          {detailTab === WO_DETAIL_TABS.OVERVIEW && (
            <Box>
              {/* Workflow Status Card */}
              <MaintenanceWorkflowStatusCard
                workOrder={selectedMaintenance}
                user={user}
                displayTimezone={displayTimezone}
                onWorkOrderRefresh={handleWorkOrderRefreshFromForm}
              />
              
              {/* Quick Audit Trail */}
              <QuickAuditTrail
                entityType="workorder"
                entityId={selectedMaintenance._id}
                entityName={`WO ${getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance.woNumber || ''}`}
                maxItems={5}
              />
              
              {/* Overview Details */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Work Order Details</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Aircraft</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedMaintenance.aircraftId?.registration || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography variant="body1" gutterBottom>{getTypeLabel(selectedMaintenance.type)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Chip
                    label={selectedMaintenance.status}
                    color={getStatusColor(selectedMaintenance.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">Priority</Typography>
                  <Chip
                    label={selectedMaintenance.priority}
                    color={getPriorityColor(selectedMaintenance.priority)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Description</Typography>
                  <Typography variant="body1">{selectedMaintenance.description || 'No description'}</Typography>
                </Grid>
                {selectedMaintenance.creationSourceRemarks && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Creation source / reason</Typography>
                    <Typography variant="body1">{selectedMaintenance.creationSourceRemarks}</Typography>
                  </Grid>
                )}
                {selectedMaintenance.linkedIntimationIds && selectedMaintenance.linkedIntimationIds.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>Linked intimations</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedMaintenance.linkedIntimationIds.map((int) => (
                        <Chip
                          key={int._id || int}
                          label={int.title ? `${int.title} (${int.status || '–'})` : `Intimation ${(int._id || int).toString().slice(-6)}`}
                          size="small"
                          variant="outlined"
                          onClick={() => navigate('/maintenance', { state: { openIntimationsTab: true, intimationId: int._id || int } })}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.CAMO_QM_REVIEW && showCamoQmReviewTab && (
            <CamoQmReviewTab
              workOrder={selectedMaintenance}
              displayTimezone={displayTimezone}
              canAcknowledge={canAcknowledge}
              canCAMOQMCompletionAck={canCAMOQMCompletionAck}
              onSuccess={handleWorkflowSuccess}
            />
          )}

          {detailTab === WO_DETAIL_TABS.WORKFLOW && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={() => handleAuthenticatedDownload(
                    `/api/maintenance/${selectedMaintenance._id}/workflow-export.csv`,
                    `workflow-export-${getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id}.csv`
                  )}
                >
                  Download Workflow CSV
                </Button>
                {selectedMaintenance?.crsDocument?.originalName && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleAuthenticatedDownload(
                      `/api/maintenance/${selectedMaintenance._id}/crs/download`,
                      selectedMaintenance.crsDocument.originalName || `crs-${selectedMaintenance._id}`
                    )}
                  >
                    Download CRS
                  </Button>
                )}
                {selectedMaintenance?.workOrderCsvDocument?.originalName && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleAuthenticatedDownload(
                      `/api/maintenance/${selectedMaintenance._id}/work-order-csv/download`,
                      selectedMaintenance.workOrderCsvDocument.originalName || `work-order-${selectedMaintenance._id}.csv`
                    )}
                  >
                    Download Uploaded CSV
                  </Button>
                )}
                {(selectedMaintenance?.attachments || []).map((att) => (
                  <Button
                    key={att.fileName}
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleAuthenticatedDownload(
                      `/api/mro/packages/${selectedMaintenance._id}/attachments/${encodeURIComponent(att.fileName)}?download=1`,
                      att.originalFileName || att.fileName
                    )}
                  >
                    {att.originalFileName || att.fileName}
                  </Button>
                ))}
                {canViewActionTakenForm(user?.role) && selectedMaintenance?.status !== 'CANCELLED' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Download />}
                    onClick={handleDownloadCameFormPdf}
                    disabled={downloadingCameFormPdf}
                  >
                    {downloadingCameFormPdf ? 'Downloading…' : 'Download CAME/FORM/04 (MRO QM)'}
                  </Button>
                )}
                {canViewActionTakenForm(user?.role) && selectedMaintenance?.status !== 'CANCELLED' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Visibility />}
                    onClick={() => setActionTakenFormOpen(true)}
                  >
                    View Action Taken Form
                  </Button>
                )}
                {selectedMaintenance?.workOrderSecondPage?.savedAt &&
                  selectedMaintenance?.workOrderSecondPage?.form && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleViewActionTakenPdf}
                      disabled={viewingActionTakenPdf || downloadingActionTakenPdf}
                    >
                      {viewingActionTakenPdf ? 'Opening…' : 'View Action Taken Form (PDF)'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      onClick={handleDownloadActionTakenPdf}
                      disabled={viewingActionTakenPdf || downloadingActionTakenPdf}
                    >
                      {downloadingActionTakenPdf ? 'Downloading…' : 'Download Action Taken Form (PDF)'}
                    </Button>
                  </>
                )}
              </Box>
              {/* Comprehensive Workflow Status */}
              <MaintenanceWorkflowStatusCard
                workOrder={selectedMaintenance}
                user={user}
                displayTimezone={displayTimezone}
                onWorkOrderRefresh={handleWorkOrderRefreshFromForm}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.LINKED && (
            <Box>
              <Typography variant="h6" gutterBottom>Linked Work Orders</Typography>
              <LinkedWorkOrders
                workOrderId={selectedMaintenance._id}
                onViewDetails={(wo) => {
                  // This will be handled by the parent component
                  console.log('View details for:', wo);
                }}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.AMP_CHECKS && hasAMPChecks && (
            <Box>
              <AMPChecksTab
                workOrder={selectedMaintenance}
                onUpdate={handleComponentUpdate}
                user={user}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.TOOLS && (
            <Box>
              <RequiredToolsEquipmentManager
                workOrderId={selectedMaintenance._id}
                workOrderLabel={getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id}
                items={selectedMaintenance.requiredToolsEquipment}
                onUpdate={handleComponentUpdate}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.SPARES && (
            <Box>
              <RequiredSparesManager
                workOrderId={selectedMaintenance._id}
                workOrderLabel={getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id}
                items={selectedMaintenance.requiredSpares}
                onUpdate={handleComponentUpdate}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.CONSUMABLES && (
            <Box>
              <RequiredConsumablesManager
                workOrderId={selectedMaintenance._id}
                workOrderLabel={getWorkOrderDisplayNumber(selectedMaintenance) || selectedMaintenance._id}
                items={selectedMaintenance.requiredConsumables}
                onUpdate={handleComponentUpdate}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.VERIFICATION && (
            <Box>
              <VerificationChecksManager
                workOrderId={selectedMaintenance._id}
                items={selectedMaintenance.verificationChecks}
                onUpdate={handleComponentUpdate}
                user={user}
              />
            </Box>
          )}

          {detailTab === WO_DETAIL_TABS.ACTIONS && (
            <Box>
              <Typography variant="h6" gutterBottom>Available Actions</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {canAcknowledge(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CheckCircleOutline />}
                    onClick={() => setDetailTab(WO_DETAIL_TABS.CAMO_QM_REVIEW)}
                  >
                    Open CAMO QM Review
                  </Button>
                )}
                {canPlan(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    startIcon={<PendingActions />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('planning', selectedMaintenance);
                    }}
                  >
                    Planning Approval
                  </Button>
                )}
                {canProvideProcurementRequirements(selectedMaintenance) && (
                  <Button
                    variant="outlined"
                    startIcon={<PendingActions />}
                    onClick={() => {
                      setDetailOpen(false);
                      setPlanningOpen(true);
                    }}
                  >
                    Provide Procurement Requirements
                  </Button>
                )}
                {canStoreAction(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('store', selectedMaintenance);
                    }}
                  >
                    Store Action
                  </Button>
                )}
                {canCreatePartsProcurement(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<Add />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('partsProcurement', selectedMaintenance);
                    }}
                  >
                    Create Parts Procurement WO
                  </Button>
                )}
                {canExternalAgencyAction?.(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<UploadFile />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('externalAgency', selectedMaintenance);
                    }}
                  >
                    External Agency Documents
                  </Button>
                )}
                {canMROAction(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('mro', selectedMaintenance);
                    }}
                  >
                    MRO Action
                  </Button>
                )}
                {canMROAssignEngineers?.(selectedMaintenance) && (
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      onOpenMroAssignEngineers?.();
                    }}
                  >
                    Manage Engineer Team
                  </Button>
                )}
                {canMROHandover?.(selectedMaintenance) && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      onOpenMroHandover?.();
                    }}
                  >
                    {Array.isArray(selectedMaintenance?.ampChecks)
                      && selectedMaintenance.ampChecks.some((c) => c.status !== 'COMPLETED')
                      ? 'Hand Over at Checks'
                      : 'Hand Over to Next Shift'}
                  </Button>
                )}
                {canMROEngineerComplete(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('mroEngineer', selectedMaintenance);
                    }}
                  >
                    MRO_ENGINEER Complete
                  </Button>
                )}
                {canMROQMCompletionAck(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CheckCircleOutline />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('mroQmCompletion', selectedMaintenance);
                    }}
                  >
                    MRO QM Completion Review
                  </Button>
                )}
                {canCAMOQMCompletionAck(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<CheckCircleOutline />}
                    onClick={() => setDetailTab(WO_DETAIL_TABS.CAMO_QM_REVIEW)}
                  >
                    Open CAMO QM Completion Review
                  </Button>
                )}
                {canComplete(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={async () => {
                      try {
                        await axios.put(`/api/maintenance/${selectedMaintenance._id}`, {
                          status: 'COMPLETED'
                        });
                        showToast('Work order marked as completed', 'success');
                        handleWorkflowSuccess();
                        setDetailOpen(false);
                      } catch (err) {
                        showToast(err.response?.data?.message || 'Failed to complete work order', 'error');
                      }
                    }}
                  >
                    Mark as Completed
                  </Button>
                )}
                {canResumeFromHold(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<CheckCircle />}
                    onClick={async () => {
                      try {
                        await axios.put(`/api/maintenance/${selectedMaintenance._id}/resume-from-hold`);
                        showToast('Work order resumed from HOLD', 'success');
                        handleWorkflowSuccess();
                        setDetailOpen(false);
                      } catch (err) {
                        showToast(err.response?.data?.message || 'Failed to resume work order', 'error');
                      }
                    }}
                  >
                    Resume from HOLD
                  </Button>
                )}
                {canIssueReleaseCertificate(selectedMaintenance) && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircle />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleWorkflowAction('releaseCertificate', selectedMaintenance);
                    }}
                  >
                    Issue Maintenance Release Certificate
                  </Button>
                )}
                {selectedMaintenance?.maintenanceRelease?.releasedAt && (
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>Certificate Issued:</strong> {selectedMaintenance.maintenanceRelease.releaseCertificate}
                    </Typography>
                    <Typography variant="caption">
                      Issued on: {new Date(selectedMaintenance.maintenanceRelease.releasedAt).toLocaleString()}
                    </Typography>
                  </Alert>
                )}
                {canEditWorkOrder?.(selectedMaintenance) && (
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleEdit(selectedMaintenance);
                    }}
                  >
                    Edit Work Order
                  </Button>
                )}
                {canCancelWorkOrder?.(selectedMaintenance) && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => {
                      setDetailOpen(false);
                      handleCancel(selectedMaintenance);
                    }}
                  >
                    Cancel Work Order
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canEditWorkOrder?.(selectedMaintenance) && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                setDetailOpen(false);
                handleEdit(selectedMaintenance);
              }}
            >
              Edit
            </Button>
          )}
          {canCancelWorkOrder?.(selectedMaintenance) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => {
                setDetailOpen(false);
                handleCancel(selectedMaintenance);
              }}
            >
              Cancel Work Order
            </Button>
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
      {actionTakenFormOpen && selectedMaintenance && (
        <WorkOrderSecondPageDialog
          open={actionTakenFormOpen}
          onClose={() => setActionTakenFormOpen(false)}
          workOrder={selectedMaintenance}
          user={user}
          onSaved={handleWorkOrderRefreshFromForm}
        />
      )}
    </Dialog>
  );
};

export default MaintenanceDetailDialog;
